import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'universal-cookie';
import type { CookieSetOptions, CookieGetOptions } from 'universal-cookie';

/**
 * Centralized cookies wrapper. Replaces the historical `js-cookie`
 * dependency with a `universal-cookie`-backed implementation that:
 *  - Participates in SSR. `<CookiesProvider>` is seeded with the request
 *    cookies on the server (see `server/ssrApp.tsx`) and with
 *    `document.cookie` in the browser (see `src/browser.tsx`), so any
 *    component / hook that reads cookies via the hooks below renders the
 *    same HTML on both sides — no hydration mismatches for cookie-driven
 *    content.
 *  - Doesn't explode in unit tests. If no provider is in scope (the bulk
 *    of the existing Jest suites render components in isolation), the
 *    hooks transparently fall back to `new Cookies()` which reads from
 *    `document.cookie` (or an empty store on the server).
 *  - Subscribes to cookie change events so components re-render when a
 *    cookie they care about flips.
 *  - Normalises `js-cookie`'s common pitfalls: `universal-cookie` auto
 *    JSON-parses (so `"true"` becomes the boolean `true`), and accepts a
 *    `Date` for `expires` rather than a day count. `useIsIrishUser()` and
 *    `cookieExpiresInDays()` paper over those gotchas.
 */

const CookiesContext = createContext<Cookies | null>(null);

export { Cookies };
export type { CookieSetOptions, CookieGetOptions };

type ProviderProps = {
    cookies?: Cookies;
    children: React.ReactNode;
};

export function CookiesProvider({ cookies, children }: ProviderProps): React.ReactElement {
    const value = useMemo(() => cookies ?? new Cookies(), [cookies]);
    return <CookiesContext.Provider value={value}>{children}</CookiesContext.Provider>;
}

/**
 * Resolve the current `Cookies` instance. Prefers the one supplied by
 * `<CookiesProvider>`; falls back to a fresh browser-backed instance so
 * the hooks keep working in tests / legacy renders that don't wrap with
 * a provider.
 */
export function useResolvedCookies(): Cookies {
    const ctx = useContext(CookiesContext);
    // The fallback intentionally creates one instance per consumer when no
    // provider is in scope (test path). That's still cheap — `new Cookies()`
    // is a thin wrapper around `document.cookie`.
    return useMemo(() => ctx ?? new Cookies(), [ctx]);
}

function useCookieValue(name: string, options?: CookieGetOptions): unknown {
    const cookies = useResolvedCookies();
    const [value, setValue] = useState<unknown>(() => cookies.get(name, { ...options, doNotUpdate: true }));
    useEffect(() => {
        const listener = () => setValue(cookies.get(name, options));
        cookies.addChangeListener(listener);
        // Re-read on mount in case the cookie was mutated between the
        // initial useState() and the effect attach (browser-only race).
        listener();
        return () => cookies.removeChangeListener(listener);
        // `options` is treated as a stable inline literal at every call
        // site; we deliberately skip it from deps to avoid resubscribing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cookies, name]);
    return value;
}

/**
 * Non-React access for utility code (RxJS epics, analytics handlers,
 * module-level data files). On the browser this reads `document.cookie`;
 * on the server it returns an empty store — those code paths shouldn't run
 * during SSR.
 */
export const getBrowserCookies = (): Cookies => new Cookies();

/**
 * The Irish-store flag is checked in dozens of components and a handful of
 * non-React utilities. Centralise the read so the truthy values
 * (`"true"`, `true`) stay in lock-step everywhere.
 */
export function useIsIrishUser(): boolean {
    const value = useCookieValue('isIrishUser', { doNotParse: true });
    return isIrishUserCookieValue(value);
}

export function isIrishUserCookieValue(value: unknown): boolean {
    return value === 'true' || value === true;
}

/**
 * Memoised string-only read for components that previously called
 * `Cookies.get(name)` and treated the result as `string | undefined`.
 */
export function useCookieString(name: string): string | undefined {
    const value = useCookieValue(name, { doNotParse: true });
    return typeof value === 'string' ? value : undefined;
}

/**
 * `js-cookie` accepted `{ expires: <number-of-days> }`. `universal-cookie`
 * wants a `Date` (or `maxAge` in seconds). Use this helper at every call
 * site to avoid quietly setting a cookie that expires at the Unix epoch.
 */
export function cookieExpiresInDays(days: number): CookieSetOptions {
    return { expires: new Date(Date.now() + days * 24 * 60 * 60 * 1000), path: '/' };
}
